import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { deleteActivityAdjustment, putActivityAdjustment } from '../api/trainer';
import { ActivityAdjustment } from '../api/types';
import { errorMessage } from '../api/errors';
import { formatDuration, parseDuration } from '../utils/format';

/** Largest official distance the backend accepts (trainer.records.max-adjustment-distance-m), in km. */
const MAX_DISTANCE_KM = 200;

interface Props {
  activityId: number;
  /** Distance Garmin recorded, in meters. Used to pre-fill the official distance. */
  trackedDistanceM: number | null;
  show: boolean;
  onClose: () => void;
  /** Called after a save or a removal, so the page can show the new state. */
  onChanged: (adjustment: ActivityAdjustment | null) => void;
}

/**
 * Official result for one activity. Use it when the run was tracked a bit long or short. The backend recomputes the
 * records from the official distance and time. There is no read endpoint for an existing adjustment, so the fields
 * start from the tracked distance and an empty time.
 */
const AdjustmentModal: React.FC<Props> = ({ activityId, trackedDistanceM, show, onClose, onChanged }) => {
  const [distanceKm, setDistanceKm] = useState('');
  const [time, setTime] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (show) {
      setDistanceKm(trackedDistanceM ? (trackedDistanceM / 1000).toFixed(3) : '');
      setTime('');
      setNote('');
      setError(null);
    }
  }, [show, trackedDistanceM]);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    const km = Number(distanceKm.replace(',', '.'));
    const seconds = parseDuration(time);
    if (!Number.isFinite(km) || km <= 0 || km > MAX_DISTANCE_KM) {
      setError(`Enter the official distance in km (above 0, up to ${MAX_DISTANCE_KM}).`);
      return;
    }
    if (seconds === null || seconds <= 0) {
      setError('Enter the official time as m:ss or h:mm:ss.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const saved = await putActivityAdjustment(activityId, {
        officialDistanceM: Math.round(km * 1000),
        officialTimeS: seconds,
        note: note.trim() || null,
      });
      onChanged(saved);
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await deleteActivityAdjustment(activityId);
      onChanged(null);
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Form onSubmit={save}>
        <Modal.Header closeButton>
          <Modal.Title>Official result</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small muted">
            Enter the distance and time you officially ran. Records use this result, and the Garmin data stays as recorded.
          </p>
          <Form.Group controlId="adjustment-distance" className="mb-3">
            <Form.Label>Official distance (km)</Form.Label>
            <Form.Control
              value={distanceKm}
              onChange={e => setDistanceKm(e.target.value)}
              inputMode="decimal"
              disabled={busy}
            />
          </Form.Group>
          <Form.Group controlId="adjustment-time" className="mb-3">
            <Form.Label>Official time (m:ss or h:mm:ss)</Form.Label>
            <Form.Control value={time} onChange={e => setTime(e.target.value)} inputMode="numeric" disabled={busy} autoFocus />
            {parseDuration(time) !== null && (
              <Form.Text className="muted">Reads as {formatDuration(parseDuration(time))}</Form.Text>
            )}
          </Form.Group>
          <Form.Group controlId="adjustment-note" className="mb-2">
            <Form.Label>Note (optional)</Form.Label>
            <Form.Control value={note} onChange={e => setNote(e.target.value)} maxLength={255} disabled={busy} />
          </Form.Group>
          {error && <Alert variant="danger" className="py-2 mt-3 mb-0">{error}</Alert>}
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <Button variant="link" className="text-danger p-0" onClick={remove} disabled={busy}>
            Remove official result
          </Button>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={busy}>
              Save official result
            </Button>
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AdjustmentModal;
